import styled, { css } from 'styled-components';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { ANIMATION_CURVE, ANIMATION_DURATION, getToken, ZIndex } from '@optimus-web/common';
import { Positioner } from '@optimus-web/helpers';
import { Button, ButtonBaseProps } from '../Button';
import { Container } from '../Container';
import { Stack, StackProps } from '../Stack';
import { BreakpointRangeWithFallback } from '../types';
import { getBreakpointRangeValueFallback } from '../utils';

export interface StyledPositionerProps {
    visible?: boolean;
    minWidth?: number;
}

export const StyledPositioner = styled(Positioner)<StyledPositionerProps>`
    display: flex;
    flex-direction: column;
    background-color: ${getToken('backgroundStaticFloating')};
    border-radius: 8px;
    box-shadow: ${getToken('shadow200')};
    opacity: 0;
    overflow-x: hidden;
    overflow-y: auto;
    outline: none;
    transition: opacity ${ANIMATION_DURATION.duration10}ms ${ANIMATION_CURVE.default};
    z-index: ${ZIndex.Higher};
    min-height: 16px;
    min-width: ${(props) => props.minWidth}px;
    ${(props) => (props.visible ? 'opacity: 1;' : 'pointer-events: none;')}

    /* Prevents content scrolling issue in Chrome */
    will-change: opacity;
`;

// ============================================================================
// Dropdown Footer
// ============================================================================

export const FooterContainer = styled(Container)`
    position: sticky;
    bottom: 0;
    background-color: ${getToken('backgroundStaticFlat')};
`;

/**
 * Logic and styles for slot Stacks
 */
interface BaseFooterSlotStackProps extends StackProps {
    breakpointRange: BreakpointRangeWithFallback;
    primary?: boolean;
}

const BaseFooterSlotStack = styled(Stack).attrs<BaseFooterSlotStackProps>(({ breakpointRange, primary }) => ({
    spacing: '150',
    itemAlignment: 'center',
    fill: true,
    vertical: isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange)),
    alignment: primary ? 'end' : 'start',
}))<BaseFooterSlotStackProps>`
    ${({ primary, breakpointRange }) =>
        primary &&
        isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange)) &&
        css`
            order: -1;
        `}
`;

export const FooterSlotStack = withBreakpointRange(BaseFooterSlotStack);

/**
 * Logic and styles for action buttons
 */

interface FooterButtonProps extends ButtonBaseProps {
    breakpointRange: BreakpointRangeWithFallback;
    children?: React.ReactNode;
}

const FooterButton = styled(Button).attrs<FooterButtonProps>(({ breakpointRange }) => {
    const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange));
    return {
        size: isMobile ? 'extraLarge' : 'small',
        wide: isMobile,
    };
})<FooterButtonProps>`
    ${({ variant, breakpointRange }) =>
        !isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange)) &&
        variant === 'primary' &&
        css`
            order: 1;
        `}
`;

// We need this type tweak because of Styled Components not recognizing the type prop in the FooterButton attrs object.
export const DropdownFooterButton = withBreakpointRange((props: Omit<FooterButtonProps, 'type'>) => (
    <FooterButton {...props} type="button" />
));
